import { useState } from "react";
import { Lock, Plus, Edit2, Trash2, Save, X, Image as ImageIcon } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { useToast } from "../hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { services as initialServices, portfolioProjects as initialProjects, aboutContent as initialAbout, adminCredentials } from "../mock";

const AdminPanel = () => {
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  
  // State for editable content
  const [services, setServices] = useState(initialServices);
  const [portfolioProjects, setPortfolioProjects] = useState(initialProjects);
  const [aboutContent, setAboutContent] = useState(initialAbout);
  
  // Edit modals
  const [editingService, setEditingService] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [isServiceDialogOpen, setIsServiceDialogOpen] = useState(false);
  const [isProjectDialogOpen, setIsProjectDialogOpen] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === adminCredentials.password) {
      setIsAuthenticated(true);
      toast({
        title: "Login Successful",
        description: "Welcome to the admin panel",
      });
    } else {
      toast({
        title: "Login Failed",
        description: "Incorrect password",
        variant: "destructive",
      });
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setPassword("");
    toast({
      title: "Logged Out",
      description: "You have been logged out successfully",
    });
  };

  // Service Management
  const handleAddService = () => {
    setEditingService({
      id: String(services.length + 1),
      title: "",
      description: "",
      icon: "Code",
      features: ["", "", "", ""]
    });
    setIsServiceDialogOpen(true);
  };

  const handleEditService = (service) => {
    setEditingService({ ...service });
    setIsServiceDialogOpen(true);
  };

  const handleSaveService = () => {
    if (services.find(s => s.id === editingService.id)) {
      setServices(services.map(s => s.id === editingService.id ? editingService : s));
      toast({
        title: "Service Updated",
        description: "Service has been updated successfully",
      });
    } else {
      setServices([...services, editingService]);
      toast({
        title: "Service Added",
        description: "New service has been added successfully",
      });
    }
    setIsServiceDialogOpen(false);
    setEditingService(null);
  };

  const handleDeleteService = (id) => {
    setServices(services.filter(s => s.id !== id));
    toast({
      title: "Service Deleted",
      description: "Service has been deleted successfully",
    });
  };

  // Project Management
  const handleAddProject = () => {
    setEditingProject({
      id: String(portfolioProjects.length + 1),
      title: "",
      category: "",
      description: "",
      image: "",
      tags: ["", "", ""],
      year: new Date().getFullYear().toString()
    });
    setIsProjectDialogOpen(true);
  };

  const handleEditProject = (project) => {
    setEditingProject({ ...project });
    setIsProjectDialogOpen(true);
  };

  const handleSaveProject = () => {
    if (portfolioProjects.find(p => p.id === editingProject.id)) {
      setPortfolioProjects(portfolioProjects.map(p => p.id === editingProject.id ? editingProject : p));
      toast({
        title: "Project Updated",
        description: "Project has been updated successfully",
      });
    } else {
      setPortfolioProjects([...portfolioProjects, editingProject]);
      toast({
        title: "Project Added",
        description: "New project has been added successfully",
      });
    }
    setIsProjectDialogOpen(false);
    setEditingProject(null);
  };

  const handleDeleteProject = (id) => {
    setPortfolioProjects(portfolioProjects.filter(p => p.id !== id));
    toast({
      title: "Project Deleted",
      description: "Project has been deleted successfully",
    });
  };

  // About Content Management
  const handleSaveAbout = () => {
    toast({
      title: "About Content Updated",
      description: "About page content has been updated successfully",
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-2">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-orange-500 rounded-2xl flex items-center justify-center mx-auto">
              <Lock className="text-white" size={32} />
            </div>
            <CardTitle className="text-2xl">Admin Panel</CardTitle>
            <p className="text-sm text-gray-600">Enter password to access</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter admin password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="border-2"
                />
                <p className="text-xs text-gray-500">Password: Oneness143</p>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold gradient-text">Admin Panel</h1>
            <Button onClick={handleLogout} variant="outline">
              Logout
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="services" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
            <TabsTrigger value="about">About</TabsTrigger>
          </TabsList>

          {/* Services Tab */}
          <TabsContent value="services" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Manage Services</h2>
              <Button onClick={handleAddService} className="bg-blue-600 hover:bg-blue-700">
                <Plus className="mr-2" size={18} /> Add Service
              </Button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {services.map((service) => (
                <Card key={service.id} className="border-2">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{service.title}</h3>
                        <Badge className="mt-2">{service.icon}</Badge>
                      </div>
                      <div className="flex space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditService(service)}
                        >
                          <Edit2 size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteService(service.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                    <div className="space-y-1">
                      {service.features.map((feature, idx) => (
                        <p key={idx} className="text-xs text-gray-500">• {feature}</p>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Manage Portfolio</h2>
              <Button onClick={handleAddProject} className="bg-orange-500 hover:bg-orange-600">
                <Plus className="mr-2" size={18} /> Add Project
              </Button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {portfolioProjects.map((project) => (
                <Card key={project.id} className="border-2 overflow-hidden">
                  <img src={project.image} alt={project.title} className="w-full h-48 object-cover" />
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <Badge variant="outline" className="text-xs mb-2">{project.category}</Badge>
                        <h3 className="font-bold text-gray-900">{project.title}</h3>
                      </div>
                      <div className="flex space-x-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditProject(project)}
                        >
                          <Edit2 size={14} />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteProject(project.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600">{project.description.substring(0, 80)}...</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* About Tab */}
          <TabsContent value="about" className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Manage About Content</h2>
              <Button onClick={handleSaveAbout} className="bg-blue-600 hover:bg-blue-700">
                <Save className="mr-2" size={18} /> Save Changes
              </Button>
            </div>

            <Card className="border-2">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <Label>Bio</Label>
                  <Textarea
                    value={aboutContent.bio}
                    onChange={(e) => setAboutContent({ ...aboutContent, bio: e.target.value })}
                    rows={4}
                    className="border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Mission</Label>
                  <Textarea
                    value={aboutContent.mission}
                    onChange={(e) => setAboutContent({ ...aboutContent, mission: e.target.value })}
                    rows={3}
                    className="border-2"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Vision</Label>
                  <Textarea
                    value={aboutContent.vision}
                    onChange={(e) => setAboutContent({ ...aboutContent, vision: e.target.value })}
                    rows={3}
                    className="border-2"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Experience</Label>
                    <Input
                      value={aboutContent.experience}
                      onChange={(e) => setAboutContent({ ...aboutContent, experience: e.target.value })}
                      className="border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Projects Completed</Label>
                    <Input
                      value={aboutContent.projectsCompleted}
                      onChange={(e) => setAboutContent({ ...aboutContent, projectsCompleted: e.target.value })}
                      className="border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Happy Clients</Label>
                    <Input
                      value={aboutContent.happyClients}
                      onChange={(e) => setAboutContent({ ...aboutContent, happyClients: e.target.value })}
                      className="border-2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Awards</Label>
                    <Input
                      value={aboutContent.awards}
                      onChange={(e) => setAboutContent({ ...aboutContent, awards: e.target.value })}
                      className="border-2"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Service Edit Dialog */}
      <Dialog open={isServiceDialogOpen} onOpenChange={setIsServiceDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingService && services.find(s => s.id === editingService.id) ? "Edit Service" : "Add Service"}
            </DialogTitle>
          </DialogHeader>
          {editingService && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingService.title}
                  onChange={(e) => setEditingService({ ...editingService, title: e.target.value })}
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingService.description}
                  onChange={(e) => setEditingService({ ...editingService, description: e.target.value })}
                  rows={3}
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Icon Name (e.g., Code, Video, Shield)</Label>
                <Input
                  value={editingService.icon}
                  onChange={(e) => setEditingService({ ...editingService, icon: e.target.value })}
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Features (4 items)</Label>
                {editingService.features.map((feature, idx) => (
                  <Input
                    key={idx}
                    value={feature}
                    onChange={(e) => {
                      const newFeatures = [...editingService.features];
                      newFeatures[idx] = e.target.value;
                      setEditingService({ ...editingService, features: newFeatures });
                    }}
                    placeholder={`Feature ${idx + 1}`}
                    className="border-2"
                  />
                ))}
              </div>
              <div className="flex space-x-3">
                <Button onClick={handleSaveService} className="flex-1 bg-blue-600 hover:bg-blue-700">
                  <Save className="mr-2" size={18} /> Save
                </Button>
                <Button onClick={() => setIsServiceDialogOpen(false)} variant="outline" className="flex-1">
                  <X className="mr-2" size={18} /> Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Edit Dialog */}
      <Dialog open={isProjectDialogOpen} onOpenChange={setIsProjectDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProject && portfolioProjects.find(p => p.id === editingProject.id) ? "Edit Project" : "Add Project"}
            </DialogTitle>
          </DialogHeader>
          {editingProject && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={editingProject.title}
                  onChange={(e) => setEditingProject({ ...editingProject, title: e.target.value })}
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <Input
                  value={editingProject.category}
                  onChange={(e) => setEditingProject({ ...editingProject, category: e.target.value })}
                  placeholder="e.g., Full Stack Development"
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={editingProject.description}
                  onChange={(e) => setEditingProject({ ...editingProject, description: e.target.value })}
                  rows={3}
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Image URL</Label>
                <Input
                  value={editingProject.image}
                  onChange={(e) => setEditingProject({ ...editingProject, image: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Input
                  value={editingProject.year}
                  onChange={(e) => setEditingProject({ ...editingProject, year: e.target.value })}
                  placeholder="2024"
                  className="border-2"
                />
              </div>
              <div className="space-y-2">
                <Label>Tags (comma-separated)</Label>
                <Input
                  value={editingProject.tags.join(", ")}
                  onChange={(e) => setEditingProject({ ...editingProject, tags: e.target.value.split(",").map(t => t.trim()) })}
                  placeholder="React, Node.js, MongoDB"
                  className="border-2"
                />
              </div>
              <div className="flex space-x-3">
                <Button onClick={handleSaveProject} className="flex-1 bg-orange-500 hover:bg-orange-600">
                  <Save className="mr-2" size={18} /> Save
                </Button>
                <Button onClick={() => setIsProjectDialogOpen(false)} variant="outline" className="flex-1">
                  <X className="mr-2" size={18} /> Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
